import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ElasticCarousel, { consts } from 'react-elastic-carousel';
import { VisuallyHidden } from '../../atoms';
import { CarouselDots } from '../CarouselDots';
import {
  StyledCarousel,
  Button,
  DotContainer,
  BottomContainer,
  ButtonContainer,
} from './styles.js';

export const Carousel = ({
  children,
  itemsToShow,
  breakPoints,
  showDots = true,
  showArrows = true,
  resetIndex,
  dotPadding,
  buttonPosition = '50',
  bottomElement,
  overlappingArrows = true,
  onArrowClick,
  onDotClick,
  dotVariant,
  onChange,
  renderPagination = ({ pages, activePage, onClick }) => {
    return (
      <BottomContainer dotPadding={dotPadding}>
        {bottomElement}
        <DotContainer>
          <CarouselDots
            pages={pages.length}
            activePage={activePage}
            onClick={page => {
              onDotClick?.(page);
              onClick(page);
            }}
            variant={dotVariant}
          />
        </DotContainer>
      </BottomContainer>
    );
  },
}) => {
  const carouselRef = useRef(null);

  useEffect(() => {
    carouselRef.current?.goTo(0);
  }, [resetIndex]);

  const customArrow = ({ type, onClick, isEdge }) => {
    const arrowType = type === consts.PREV ? 'left' : 'right';
    return (
      <ButtonContainer>
        <Button
          onClick={() => {
            onArrowClick?.(arrowType);
            onClick();
          }}
          disabled={isEdge}
          arrowType={arrowType}
          buttonPosition={buttonPosition}
          overlappingArrows={overlappingArrows}
        >
          <VisuallyHidden>{`${arrowType} arrow`}</VisuallyHidden>
        </Button>
      </ButtonContainer>
    );
  };

  return (
    <StyledCarousel>
      <ElasticCarousel
        ref={carouselRef}
        itemPadding={overlappingArrows ? [0, 0, 0, 0] : [8, 8, 8, 8]}
        itemsToShow={itemsToShow}
        breakPoints={breakPoints}
        renderArrow={customArrow}
        pagination={showDots}
        renderPagination={renderPagination}
        showArrows={showArrows}
        onChange={onChange}
      >
        {children}
      </ElasticCarousel>
    </StyledCarousel>
  );
};

Carousel.displayName = 'Carousel';

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  itemsToShow: PropTypes.number,
  breakPoints: PropTypes.arrayOf(
    PropTypes.shape({
      width: PropTypes.number,
      itemsToShow: PropTypes.number,
      itemsToScroll: PropTypes.number,
    }),
  ),
  showDots: PropTypes.bool,
  showArrows: PropTypes.bool,
  /** Provide a value here and change it if you want the carousel to reset to beginning. */
  resetIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  buttonPosition: PropTypes.oneOf(['15', '17', '30', '40', '45', '50']),
  dotPadding: PropTypes.number,
  dotVariant: PropTypes.string,
  bottomElement: PropTypes.node,
  overlappingArrows: PropTypes.bool,
  onArrowClick: PropTypes.func,
  onDotClick: PropTypes.func,
  onChange: PropTypes.func,
  renderPagination: PropTypes.func,
};
